/**
 * This is copied from `src/sections/profile/utils/use-profile-navigation-props` but uses
 * the navigation API resource instead of redux to populate its data. Any changes made here
 * should be synced to that file as well, until all workspace pages are able to use the
 * react-resource-router and the navigation resource (then that file can be deleted).
 */
import { useContext, useMemo } from 'react';

import qs from 'qs';
import { useResource } from 'react-resource-router';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import {
  addMenuItemsById,
  createNestedMenu,
  findNestedMenuItem,
  MenuItem,
  ROOT_MENU_ID,
} from '@atlassian/bitbucket-navigation';

import { ComponentLinkProxy } from 'src/components/component-link';
import { pathnameMatchesForgeMenuItemUrl } from 'src/components/forge-ui/src/utils/utils';
import { ContainerNavigationProps } from 'src/components/navigation/src/components/container-navigation-v3';
import { FORGE_MENU_ITEM_TYPE } from 'src/components/navigation/src/utils/create-nested-menu';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { ForgeAppsContext } from 'src/contexts/forge-apps-provider';
import { useLocation, useMatch, useParams } from 'src/router/hooks';
import {
  WorkspaceNavigation,
  workspaceNavigationResource,
} from 'src/sections/workspace/workspace-navigation-resource';
import { publishUiEvent } from 'src/utils/analytics/publish';

/**
 * Note right now using menu item out of workspace nav resource means
 * that while the resource is loading, we won't have menu items displayed.
 * This is only an issue while we are in the half-half state of only
 * some pages having SSR enabled, if/when all pages are SSR enabled &
 * also using workspaceNavigationResource, the workspace nav resource state
 * should already be populated on server side render so menu items would also
 * already be in the resource state.
 */
const useMenuItemsFromResource = (
  workspaceNavData: WorkspaceNavigation | null
) => {
  if (workspaceNavData) {
    return {
      menuItemsFromResource: workspaceNavData.menuItems.sidebar,
      workspace: workspaceNavData.workspace,
    };
  }
  return { menuItemsFromResource: [], workspace: undefined };
};

type WorkspaceMenuItems = {
  menuItems: MenuItem[];
  selectedMenuItem: MenuItem | undefined;
};

export const useWorkspaceMenuItems = (): WorkspaceMenuItems => {
  const { data, error } = useResource(workspaceNavigationResource);
  const { menuItemsFromResource, workspace } = useMenuItemsFromResource(data);
  const location = useLocation();
  const match = useMatch();
  const enableForgeHistory = useStatsigGate(
    StatsigFeatureKeys.enableForgeHistory
  );

  const forgeAppsContext = useContext(ForgeAppsContext);

  // memoizing menuItems to avoid unnecessary re-rendering on workspace settings pages
  // (e.g. rerender on groups/:groupSlug page causes the tab to reset to users when you
  // try to select propjects & repositories)
  const menuItems = useMemo(() => {
    if (!menuItemsFromResource || !menuItemsFromResource.length) {
      return [];
    }
    let items = createNestedMenu(menuItemsFromResource);

    const forgeAdminMenuItems = forgeAppsContext.workspaceForgeMenuItems;

    if (forgeAdminMenuItems && forgeAdminMenuItems.length > 0) {
      items = addMenuItemsById(items, ROOT_MENU_ID, forgeAdminMenuItems);
    }
    return items;
  }, [menuItemsFromResource, forgeAppsContext.workspaceForgeMenuItems]);

  // TODO: Add dedicated error handling
  if (error || !workspace) {
    return { menuItems: [], selectedMenuItem: undefined };
  }

  let { pathname } = location;
  let matchUrl = match.url;
  // check if url contains uuid and replace with slug to match menuItems
  if (pathname.includes(workspace.uuid)) {
    pathname = pathname.replace(workspace.uuid, workspace.slug);
    matchUrl = matchUrl.replace(workspace.uuid, workspace.slug);
  }
  const path = pathname.replace(/\/$/, '');

  const selectedMenuItem = findNestedMenuItem(menuItems, (item: MenuItem) => {
    const workspaceSubpath = pathname.slice(matchUrl.length);
    const prefixes = item.matching_url_prefixes || [];
    const itemUrl = item.url.replace(/\/$/, '');

    if (enableForgeHistory && item.type === FORGE_MENU_ITEM_TYPE) {
      return pathnameMatchesForgeMenuItemUrl(path, itemUrl);
    }

    return (
      path === itemUrl ||
      prefixes.some(
        prefix =>
          workspaceSubpath.startsWith(prefix) || // match if subpath starts with prefix
          (path.startsWith(itemUrl) && prefix === '*') || // match if path startsWith itemUrl and prefix is *
          path === prefix // accept override if entire path matches prefix (e.g /some-workspace-name)
      )
    );
  });

  return { menuItems, selectedMenuItem };
};

export const useWorkspaceContainerNavigationState =
  (): ContainerNavigationProps => {
    const { menuItems, selectedMenuItem } = useWorkspaceMenuItems();

    return {
      linkComponent: ComponentLinkProxy,
      menuItems,
      publishUiEvent,
      selectedMenuItem,
    };
  };

type WorkspaceNavActionUrls = {
  createProjectUrl?: string;
  createRepoUrl?: string;
  importRepoUrl?: string;
};

/** Returns actions user has permission to take. If the BE does not include the
 * action in the links object (data.links), it means user is not authorized to take that action
 */
export const useWorkspaceNavActionUrls = (): WorkspaceNavActionUrls => {
  const { data, error, loading } = useResource(workspaceNavigationResource);
  const { projectKey } = useParams<{
    projectKey: string;
  }>();

  if (!data || error || loading) {
    return {};
  }

  const { links } = data;
  // add the project key as a query param if user is currently
  // in the context of a specific project
  const projectQuery = qs.stringify(
    { project: projectKey },
    {
      skipNulls: true,
      addQueryPrefix: true,
    }
  );

  return {
    createProjectUrl: links?.create_project?.href,
    createRepoUrl:
      links?.create_repository?.href &&
      `${links?.create_repository?.href}${projectQuery}`,
    importRepoUrl:
      links?.import_repository?.href &&
      `${links?.import_repository?.href}${projectQuery}`,
  };
};
